import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { Copy, Check, Heart, Flame, Sparkles, Utensils, ArrowLeft, Loader2, Share2, Link as LinkIcon, Link2, Users, Zap, Library, PenLine, MessageCircle, MessageSquare, Mic, Lock, Video, ChevronDown, Clock, RefreshCw, CheckCircle2, Hourglass } from "lucide-react";
import { copyText, shareOrCopy, openExternal, composeInvite } from "../lib/clipboard";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";
import { useRoomSync } from "../lib/useRoomSync";
import ScoreCard from "../components/ScoreCard";
import { toast } from "sonner";
import FloatingHearts from "../components/FloatingHearts";
import Header from "../components/Header";
import VoiceRecorder from "../components/VoiceRecorder";
import VideoRecorder from "../components/VideoRecorder";
import ConfettiBurst from "../components/ConfettiBurst";
import PushOptInSheet from "../components/PushOptInSheet";
import NotFound from "./NotFound";
import { useBilling } from "../context/BillingContext";

export default function Room() {
  const { code } = useParams();
  const nav = useNavigate();
  const { openPaywall, refreshUsage } = useBilling();
  const [error, setError] = useState(null);
  const [role] = useState(() => localStorage.getItem("player_role") || "host");
  const [playerName] = useState(() => localStorage.getItem("player_name") || "");

  const missingCountRef = useRef(0);
  const prevTypedAnswerRef = useRef(null);
  const prevTurnRef = useRef(null);
  const prevModeRef = useRef(null);
  const prevGuestRef = useRef(null);
  const prevReactionRef = useRef(null);

  // SSE-backed room sync. Falls back to 2s polling automatically.
  const { data: room, refresh: fetchRoom } = useRoomSync({
    code,
    endpoint: "rooms",
    fetchFn: useCallback(() => api.getRoom(code), [code]),
    onError: useCallback((e) => {
      // Only declare a room "expired" after THREE consecutive 404s — protects
      // against transient network blips and serverless cold-starts.
      const status = e?.response?.status;
      if (status === 404) {
        missingCountRef.current += 1;
        if (missingCountRef.current >= 3) setError("Room not found");
      }
    }, []),
  });

  // Partner notifications — fire toasts when the OTHER player makes a move
  // we should know about: starts answering, hands turn back, joins, or leaves.
  useEffect(() => {
    if (!room) return;
    const partner = role === "host" ? "guest" : "host";
    const partnerName = role === "host" ? room.guest_name : room.host_name;

    // Partner submitted an answer (their turn → answer revealed)
    const ta = room.typed_answer;
    if (
      ta &&
      ta.player === partner &&
      (!prevTypedAnswerRef.current ||
        prevTypedAnswerRef.current.player !== partner ||
        prevTypedAnswerRef.current.answer !== ta.answer)
    ) {
      const kind =
        ta.answer_type === "voice"
          ? "voice note"
          : ta.answer_type === "video"
          ? "view-once video 🔥"
          : "answer";
      toast.success(`${partnerName} sent their ${kind}`, {
        description: ta.answer_type === "video" ? "It vanishes after one watch." : undefined,
        duration: 3500,
      });
    }
    prevTypedAnswerRef.current = ta;

    // Turn just flipped to ME (partner accepted/skipped, or hit Next round)
    if (
      prevTurnRef.current &&
      prevTurnRef.current !== room.turn &&
      room.turn === role &&
      room.status === "playing"
    ) {
      toast(`Your turn 👀`, {
        description: `${partnerName} just played — your move now.`,
        duration: 2800,
      });
    }
    prevTurnRef.current = room.turn;

    // Partner picked a pack (lobby → playing transition we didn't initiate)
    if (
      prevModeRef.current === null &&
      room.mode &&
      role === "guest" &&
      room.status === "playing"
    ) {
      // First time mode arrives on guest side
      toast(`${partnerName} picked the vibe 🔥`, {
        description: room.pack_name ? `Pack: ${room.pack_name}` : undefined,
        duration: 3000,
      });
    }
    prevModeRef.current = room.mode;

    // Guest joined (host's pov)
    if (
      role === "host" &&
      !prevGuestRef.current &&
      room.guest_name &&
      room.status === "lobby"
    ) {
      toast.success(`${room.guest_name} joined 💕`, {
        description: "Pick a pack — game starts as soon as you tap.",
        duration: 3500,
      });
    }
    prevGuestRef.current = room.guest_name;

    // Partner reacted to my answer (chat-thread reveal)
    const newReaction = room.typed_answer?.reaction;
    if (
      newReaction &&
      newReaction.by === partner &&
      (!prevReactionRef.current || prevReactionRef.current.ts !== newReaction.ts)
    ) {
      toast(`${partnerName} reacted 💬`, {
        description: `"${newReaction.text}"`,
        duration: 3500,
      });
    }
    prevReactionRef.current = newReaction;
  }, [room, role]);

  if (error) {
    return <NotFound kind="room" />;
  }

  if (!room) {
    return (
      <div className="warm-bg min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#FF4D8D]" />
      </div>
    );
  }

  return (
    <div className="warm-bg min-h-screen relative overflow-hidden">
      <FloatingHearts count={6} />
      <Header />
      <main className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 py-10">
        <button
          onClick={() => nav("/")}
          className="flex items-center gap-2 text-[#C9C7D9] hover:text-white font-semibold mb-8 transition"
          data-testid="room-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Leave room
        </button>

        {room.status === "waiting" && <LobbyView room={room} role={role} refresh={fetchRoom} />}
        {room.status === "lobby" && <LobbyView room={room} role={role} refresh={fetchRoom} />}
        {["playing", "answer_revealed", "quiz_answering", "quiz_reveal"].includes(room.status) && (
          <GameplayView room={room} role={role} playerName={playerName} refresh={fetchRoom} />
        )}
      </main>

      <PushOptInSheet
        open={!!room}
        partnerName={role === "host" ? room.guest_name : room.host_name}
      />
    </div>
  );
}

function LobbyView({ room, role, refresh }) {
  const { isPackUnlocked, openPaywall, openPackPreview } = useBilling();
  // Mode is fixed to truth_or_dare — all 10 packs have rich truth_or_dare
  // content (cute, flirty, spicy, missions, dares). The previous 3-mode
  // hero picker added friction without value, so it's been removed.
  const mode = "truth_or_dare";
  const spice = "flirty";
  const source = "pack";
  const [packId, setPackId] = useState(null);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const isHost = role === "host";

  useEffect(() => {
    api.listPacks().then(setPacks).catch(() => {});
    // Apply pre-selected pack from landing prompt (if any)
    if (isHost) {
      const prePack = localStorage.getItem("preselected_pack_id");
      if (prePack) setPackId(prePack);
      // Consume once
      localStorage.removeItem("preselected_mode");
      localStorage.removeItem("preselected_source");
      localStorage.removeItem("preselected_pack_id");
    }
  }, [isHost]);

  // Auto-fire the share intent chosen on the entry page once the room exists
  useEffect(() => {
    if (!isHost) return;
    const intent = localStorage.getItem("remote_share_intent");
    if (!intent) return;
    localStorage.removeItem("remote_share_intent");
    const joinUrl = `${window.location.origin}/join/${room.code}`;
    const inviteText = composeInvite(room.host_name, joinUrl);
    (async () => {
      if (intent === "copy") {
        const ok = await copyText(joinUrl);
        if (ok) toast.success("Link copied — paste it anywhere 💌");
      } else if (intent === "share") {
        await shareOrCopy({
          title: "VibeCheck — join me",
          text: inviteText,
          url: joinUrl,
        });
      } else if (intent === "sms") {
        await openExternal(`sms:?&body=${encodeURIComponent(inviteText)}`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.code]);

  const startWithPack = useCallback(
    async (pickedPackId) => {
      if (!pickedPackId) return;
      if (!isPackUnlocked(pickedPackId)) {
        openPaywall("pack_locked", pickedPackId);
        return;
      }
      setPackId(pickedPackId);
      setLoading(true);
      try {
        await api.setMode(room.code, {
          mode,
          spice_level: spice,
          source,
          pack_id: pickedPackId,
        });
        refresh();
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 402 && detail?.code === "premium_pack_locked") {
          openPaywall("pack_locked", detail.pack_id);
        } else {
          toast.error(typeof detail === "string" ? detail : "Couldn't start");
        }
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [room.code, isPackUnlocked, openPaywall, refresh]
  );

  // Auto-start with the pre-selected pack as soon as both players are in the lobby.
  // The pack id is set on the entry pick-pack page (PlayRemotelyPickPack).
  useEffect(() => {
    if (!isHost) return;
    const preset = localStorage.getItem("remote_preselected_pack");
    if (!preset) return;
    if (!room.guest_name) return;
    localStorage.removeItem("remote_preselected_pack");
    startWithPack(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.guest_name]);


  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="remotely-lobby-screen">
      {/* Waiting Room — cinematic two-avatar connection */}
      <WaitingRoomHero room={room} isHost={isHost} loading={loading} />

      {/* Fallback pack-picker — only shown if the auto-start didn't trigger
          (e.g. user landed here without going through PlayRemotelyPickPack).
          With normal flow this stays hidden. */}
      {isHost && room.guest_name && !localStorage.getItem("remote_preselected_pack") && room.status !== "playing" && (
        <div className="card-glow rounded-3xl p-6 md:p-8 max-w-2xl mx-auto mt-6">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#C9C7D9] mb-5 text-center">
            {loading ? "Starting…" : "Pick a pack"}
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {packs.map((p) => (
              <LobbyPackTile
                key={p.id}
                pack={p}
                unlocked={isPackUnlocked(p.id)}
                active={packId === p.id}
                onSelect={() => startWithPack(p.id)}
                onLocked={() => openPackPreview(p)}
              />
            ))}
          </div>
          {loading && (
            <div className="flex items-center justify-center gap-2 mt-6 text-[#C9C7D9] text-sm font-semibold">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading first round…
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

/**
 * Cinematic Waiting Room hero — matches the user's reference design:
 *  - Title "Waiting for them…" with gradient pink
 *  - Two avatars (host + guest) connected by a glowing neon heart
 *  - Concentric pulse rings radiating from the heart
 *  - Status chips: "{Name} joined 💜" + contextual second chip
 *  - Footer copy: "This is where things get interesting…"
 */
function WaitingRoomHero({ room, isHost, loading }) {
  const AVATAR_MAP = {
    "pink-w":   "/assets/home/avatar-pink-w.png",
    "purple-w": "/assets/home/avatar-purple-w.png",
    "orange-w": "/assets/home/avatar-orange-w.png",
    "red-m":    "/assets/home/avatar-red-m.png",
    "blue-m":   "/assets/home/avatar-blue-m.png",
    "green-m":  "/assets/home/avatar-green-m.png",
  };
  // From the viewer's POV: YOU = the current player; "your person" = partner
  const youAvatar     = AVATAR_MAP[isHost ? room.host_avatar_id  : room.guest_avatar_id]  || AVATAR_MAP["pink-w"];
  const partnerAvatar = AVATAR_MAP[isHost ? room.guest_avatar_id : room.host_avatar_id]   || AVATAR_MAP["blue-m"];
  const partnerJoined = !!(isHost ? room.guest_name : room.host_name);
  const partnerName   = isHost ? room.guest_name : room.host_name;

  // "Invite sent ✉️" flash pill — only briefly shown when host arrives from /invite
  const [showInviteSent, setShowInviteSent] = useState(false);
  useEffect(() => {
    if (isHost && sessionStorage.getItem("vc_invite_just_sent") === "1") {
      sessionStorage.removeItem("vc_invite_just_sent");
      setShowInviteSent(true);
      const t = setTimeout(() => setShowInviteSent(false), 3200);
      return () => clearTimeout(t);
    }
  }, [isHost]);

  // Status stepper — animates across 4 phases on a 10s loop until partner joins
  const STEPS = ["Checking invite…", "Waiting for them…", "Almost there…", "Still waiting…"];
  const [stepIdx, setStepIdx] = useState(1);
  useEffect(() => {
    if (partnerJoined || loading) return;
    const t = setInterval(() => setStepIdx((i) => (i + 1) % STEPS.length), 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerJoined, loading]);

  const copyInviteAgain = async () => {
    const url = `${window.location.origin}/join/${room.code}`;
    const ok = await copyText(url);
    if (ok) toast.success("Link copied — paste it anywhere 💌");
  };
  const shareWhatsApp = async () => {
    const url = `${window.location.origin}/join/${room.code}`;
    await openExternal(`https://wa.me/?text=${encodeURIComponent(composeInvite(room.host_name, url))}`);
  };

  return (
    <div className="relative" data-testid="waiting-room-hero">
      {/* "Invite sent ✉️" flash pill — top center */}
      <AnimatePresence>
        {showInviteSent && (
          <motion.div
            key="invite-sent-flash"
            initial={{ opacity: 0, y: -16, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.94 }}
            transition={{ type: "spring", stiffness: 320, damping: 24 }}
            className="absolute left-1/2 -translate-x-1/2 -top-12 z-[10]"
            data-testid="waiting-room-invite-sent-flash"
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-[13px] font-extrabold"
              style={{
                background: "rgba(15, 8, 22, 0.92)",
                border: "1.5px solid rgba(255, 46, 154, 0.65)",
                boxShadow: "0 0 18px rgba(255, 46, 154, 0.55), 0 6px 22px rgba(0, 0, 0, 0.45)",
                backdropFilter: "blur(10px)",
              }}
            >
              <CheckCircle2 className="w-4 h-4" style={{ color: "#FF2E9A" }} strokeWidth={2.4} />
              Invite sent <span style={{ filter: "drop-shadow(0 0 4px #FF2E9A)" }}>💌</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Title block */}
      <div className="text-center mb-6 mt-3">
        <h1
          className="font-extrabold leading-[1.0] tracking-tight mb-2"
          style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: "clamp(2.0rem, 8vw, 2.6rem)",
            letterSpacing: "-0.025em",
            background: partnerJoined
              ? "linear-gradient(90deg, #FFD9C2 0%, #FF9F7A 45%, #FF6B47 100%)"
              : "linear-gradient(90deg, #FFC2E0 0%, #FF8FC8 45%, #FF2E9A 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            filter: partnerJoined
              ? "drop-shadow(0 0 18px rgba(255, 107, 71, 0.7))"
              : "drop-shadow(0 0 18px rgba(255, 46, 154, 0.7))",
          }}
          data-testid="waiting-room-title"
        >
          {partnerJoined ? (
            <>Both of you are here <span style={{ filter: "drop-shadow(0 0 8px #FF6B47)" }}>✨</span></>
          ) : (
            <>Waiting for them…</>
          )}
        </h1>
        <p className="text-white/75 text-[14px] font-medium">
          {partnerJoined ? (
            <>The chaos is about to start <motion.span animate={{ scale: [1, 1.18, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} className="inline-block ml-1">🔥</motion.span></>
          ) : (
            <>The chaos starts when they join <motion.span animate={{ scale: [1, 1.18, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} className="inline-block ml-1">👀</motion.span></>
          )}
        </p>
      </div>

      {/* Avatars + animated connection */}
      <div className="relative h-[230px] flex items-center justify-center mb-6" data-testid="waiting-room-connection">
        {/* Concentric pulse rings around center */}
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            aria-hidden
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 100 + i * 60,
              height: 100 + i * 60,
              border: `1.5px solid rgba(255, 46, 154, ${0.5 - i * 0.12})`,
              boxShadow: `0 0 ${16 - i * 4}px rgba(255, 46, 154, ${0.35 - i * 0.1})`,
            }}
            animate={{ scale: [1, 1.14, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2.6 + i * 0.4, repeat: Infinity, delay: i * 0.25, ease: "easeInOut" }}
          />
        ))}

        {/* Dotted flowing connection line (host → heart → partner) */}
        <svg
          className="absolute pointer-events-none w-full"
          height="60"
          viewBox="0 0 400 60"
          preserveAspectRatio="none"
          style={{ top: "50%", transform: "translateY(-50%)", filter: "drop-shadow(0 0 5px rgba(255, 46, 154, 0.85))" }}
          aria-hidden
        >
          {/* Static base */}
          <line x1="80" y1="30" x2="320" y2="30" stroke="rgba(255, 46, 154, 0.25)" strokeWidth="2.5" strokeDasharray="3 7" />
          {/* Left half: host → heart */}
          <line x1="80" y1="30" x2="200" y2="30" stroke="#FF2E9A" strokeWidth="3" strokeDasharray="3 7" strokeLinecap="round">
            <animate attributeName="stroke-dashoffset" from="10" to="0" dur="0.9s" repeatCount="indefinite" />
          </line>
          {/* Right half: heart → partner */}
          <line x1="200" y1="30" x2="320" y2="30" stroke="#FF2E9A" strokeWidth="3" strokeDasharray="3 7" strokeLinecap="round">
            <animate attributeName="stroke-dashoffset" from="0" to="10" dur="0.9s" repeatCount="indefinite" />
          </line>
        </svg>

        {/* YOU — left avatar */}
        <div
          className="absolute z-[3] flex flex-col items-center"
          style={{ left: "8%", top: "50%", transform: "translateY(-50%)" }}
        >
          <motion.div
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="relative w-[108px] h-[108px] rounded-full overflow-hidden"
            style={{
              border: "3px solid #FF2E9A",
              boxShadow: "0 0 28px rgba(255, 46, 154, 0.85), 0 0 60px rgba(255, 46, 154, 0.35), inset 0 0 10px rgba(0,0,0,0.3)",
            }}
          >
            <img src={youAvatar} alt="You" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </motion.div>
          <div
            className="mt-1.5 inline-flex items-center gap-1 px-3 py-[3px] rounded-full text-white text-[11px] font-extrabold"
            style={{
              background: "rgba(15, 8, 22, 0.85)",
              border: "1.5px solid #FF2E9A",
              boxShadow: "0 0 10px rgba(255, 46, 154, 0.5)",
            }}
          >
            <Heart className="w-3 h-3" style={{ color: "#FF2E9A", fill: "#FF2E9A" }} strokeWidth={2} />
            You
          </div>
        </div>

        {/* Center heart */}
        <motion.div
          className="relative z-[4]"
          animate={{ scale: [1, 1.18, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Heart
            className="w-14 h-14"
            style={{
              color: "#FF2E9A",
              fill: "#FF2E9A",
              filter: "drop-shadow(0 0 16px #FF2E9A) drop-shadow(0 0 36px rgba(255, 46, 154, 0.7))",
            }}
            strokeWidth={2}
          />
        </motion.div>

        {/* PARTNER — right avatar */}
        <div
          className="absolute z-[3] flex flex-col items-center"
          style={{ right: "8%", top: "50%", transform: "translateY(-50%)" }}
        >
          {partnerJoined ? (
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, y: [0, -4, 0] }}
              transition={{
                scale: { type: "spring", stiffness: 320, damping: 22 },
                opacity: { duration: 0.4 },
                y: { duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.5 },
              }}
              className="w-[108px] h-[108px] rounded-full overflow-hidden"
              style={{
                border: "3px solid #9B6BFF",
                boxShadow: "0 0 28px rgba(155, 107, 255, 0.85), 0 0 60px rgba(155, 107, 255, 0.35), inset 0 0 10px rgba(0,0,0,0.3)",
              }}
            >
              <img src={partnerAvatar} alt={partnerName || "Partner"} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </motion.div>
          ) : (
            <motion.div
              animate={{ scale: [1, 1.06, 1], opacity: [0.55, 1, 0.55] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="w-[108px] h-[108px] rounded-full flex items-center justify-center"
              style={{
                background: "rgba(20, 14, 26, 0.65)",
                border: "3px solid #9B6BFF",
                boxShadow: "0 0 24px rgba(155, 107, 255, 0.75), inset 0 0 10px rgba(0,0,0,0.4)",
                backdropFilter: "blur(8px)",
              }}
            >
              {/* User silhouette */}
              <svg width="50" height="58" viewBox="0 0 50 58" aria-hidden style={{ filter: "drop-shadow(0 0 6px #9B6BFF)" }}>
                <circle cx="25" cy="16" r="11" fill="#9B6BFF" opacity="0.7" />
                <path d="M 5 56 C 5 38, 45 38, 45 56 Z" fill="#9B6BFF" opacity="0.7" />
              </svg>
            </motion.div>
          )}
          <div
            className="mt-1.5 inline-flex items-center gap-1 px-3 py-[3px] rounded-full text-white text-[11px] font-extrabold"
            style={{
              background: "rgba(15, 8, 22, 0.85)",
              border: "1.5px solid #9B6BFF",
              boxShadow: "0 0 10px rgba(155, 107, 255, 0.5)",
            }}
          >
            {partnerJoined && partnerName ? partnerName : "Your person"}
            <Heart className="w-3 h-3" style={{ color: "#9B6BFF", fill: "#9B6BFF" }} strokeWidth={2} />
          </div>
        </div>
      </div>

      {/* Status card — glassmorphic */}
      <div
        className="relative rounded-2xl p-4 md:p-5 mb-5 mx-auto max-w-md flex items-center gap-3"
        style={{
          background: "rgba(20, 14, 30, 0.7)",
          border: "1.5px solid rgba(155, 107, 255, 0.45)",
          boxShadow: "0 0 22px rgba(155, 107, 255, 0.25), inset 0 0 12px rgba(155, 107, 255, 0.08)",
          backdropFilter: "blur(12px)",
        }}
        data-testid="waiting-room-status-card"
      >
        {/* Left: orbiting heart in dashed circle */}
        <div className="relative w-[60px] h-[60px] flex items-center justify-center flex-shrink-0">
          <motion.div
            aria-hidden
            className="absolute w-full h-full rounded-full"
            style={{ border: "1.5px dashed rgba(255, 46, 154, 0.55)" }}
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          />
          <motion.div
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <Heart className="w-6 h-6" style={{ color: "#FF2E9A", fill: "#FF2E9A", filter: "drop-shadow(0 0 6px #FF2E9A)" }} strokeWidth={2} />
          </motion.div>
        </div>

        {/* Middle: text */}
        <div className="flex-1 min-w-0">
          <div className="text-white text-[15px] font-extrabold leading-tight mb-1.5">
            Waiting for{" "}
            <span style={{ color: "#9B6BFF", filter: "drop-shadow(0 0 5px #9B6BFF)" }}>
              {partnerJoined ? partnerName : "your person"}
            </span>{" "}
            <span style={{ filter: "drop-shadow(0 0 4px #9B6BFF)" }}>💜</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/85 text-[12px] font-semibold mb-0.5">
            <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#34D399" }} strokeWidth={2.4} />
            Link sent
          </div>
          <div className="flex items-center gap-1.5 text-white/65 text-[12px] font-medium">
            <Clock className="w-3.5 h-3.5" />
            Room expires in 24h
          </div>
        </div>

        {/* Right: hourglass with hearts */}
        <motion.div
          animate={{ rotate: [0, 0, 180, 180, 360] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", times: [0, 0.45, 0.5, 0.95, 1] }}
          className="flex-shrink-0"
          style={{ filter: "drop-shadow(0 0 10px rgba(255, 46, 154, 0.7))" }}
        >
          <Hourglass className="w-8 h-8" style={{ color: "#FF2E9A" }} strokeWidth={1.8} fill="rgba(255, 46, 154, 0.15)" />
        </motion.div>
      </div>

      {/* Status stepper row */}
      <div
        className="rounded-full mx-auto max-w-md mb-5 px-3 py-2 overflow-x-auto"
        style={{
          background: "rgba(20, 14, 30, 0.55)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(8px)",
        }}
        data-testid="waiting-room-stepper"
      >
        <div className="flex items-center gap-3 whitespace-nowrap justify-center text-[12px] font-bold">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
          >
            <RefreshCw className="w-3.5 h-3.5" style={{ color: "#FF2E9A" }} strokeWidth={2.5} />
          </motion.div>
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-3">
              <span
                style={{
                  color: i === stepIdx ? "#FF2E9A" : "rgba(255,255,255,0.5)",
                  textShadow: i === stepIdx ? "0 0 8px rgba(255, 46, 154, 0.7)" : "none",
                  transition: "color 0.4s, text-shadow 0.4s",
                }}
              >
                {s}
              </span>
              {i < STEPS.length - 1 && (
                <span className="w-1 h-1 rounded-full" style={{ background: "rgba(255,255,255,0.4)" }} aria-hidden />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Share-again CTAs */}
      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mb-5">
        <button
          onClick={copyInviteAgain}
          data-testid="waiting-room-copy-again"
          className="rounded-2xl py-3 px-3 font-extrabold text-white flex items-center justify-center gap-2 transition active:scale-[0.98]"
          style={{
            background: "rgba(15, 8, 22, 0.55)",
            border: "2px solid #9B6BFF",
            boxShadow: "0 0 14px rgba(155, 107, 255, 0.5)",
            fontSize: "13.5px",
          }}
        >
          <LinkIcon className="w-4 h-4" style={{ color: "#C088FF" }} strokeWidth={2.4} />
          Copy Invite Again
        </button>
        <button
          onClick={shareWhatsApp}
          data-testid="waiting-room-share-whatsapp"
          className="rounded-2xl py-3 px-3 font-extrabold text-white flex items-center justify-center gap-2 transition active:scale-[0.98]"
          style={{
            background: "rgba(15, 8, 22, 0.55)",
            border: "2px solid #FF2E9A",
            boxShadow: "0 0 16px rgba(255, 46, 154, 0.55)",
            fontSize: "13.5px",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 32 32" aria-hidden>
            <path fill="#25D366" d="M16 .5C7.44.5.5 7.44.5 16c0 2.8.74 5.43 2.04 7.71L0 32l8.5-2.46A15.49 15.49 0 0 0 16 31.5c8.56 0 15.5-6.94 15.5-15.5S24.56.5 16 .5Z" />
            <path fill="#FFFFFF" d="M16 4.5C9.65 4.5 4.5 9.65 4.5 16c0 2.43.76 4.69 2.05 6.55l-1.34 4.9 5.02-1.32A11.45 11.45 0 0 0 16 27.5c6.35 0 11.5-5.15 11.5-11.5S22.35 4.5 16 4.5Zm6.69 16.21c-.28.79-1.62 1.5-2.27 1.6-.6.08-1.37.12-2.21-.14-.51-.16-1.17-.39-2.01-.74-3.54-1.53-5.85-5.1-6.03-5.34-.18-.24-1.45-1.93-1.45-3.68 0-1.75.92-2.6 1.24-2.96.32-.36.7-.45.93-.45h.66c.21 0 .5-.08.78.6.28.69.96 2.39 1.05 2.56.09.18.14.39.03.62-.11.24-.16.39-.32.6-.16.21-.34.46-.49.62-.16.16-.33.34-.14.66.18.32.81 1.34 1.74 2.18 1.2 1.07 2.21 1.4 2.53 1.56.32.16.51.13.7-.08.18-.2.81-.94.99-1.27.18-.32.37-.27.62-.16.25.11 1.59.75 1.87.89.28.13.46.2.53.31.07.11.07.66-.21 1.45Z" />
          </svg>
          Share on WhatsApp
        </button>
      </div>

      {/* Privacy strip */}
      <div className="flex items-center justify-center gap-3 text-white/65 text-[12px] font-semibold mb-4">
        <span className="inline-flex items-center gap-1.5">
          <Lock className="w-[13px] h-[13px]" />
          Private room
        </span>
        <span className="w-1 h-1 rounded-full bg-white/30" aria-hidden />
        <span className="inline-flex items-center gap-1.5">
          Just between you two
          <span style={{ filter: "drop-shadow(0 0 5px #9B6BFF)" }}>💜</span>
        </span>
      </div>

      {/* Helper chip */}
      <div
        className="rounded-2xl mx-auto max-w-md py-3 px-4 text-center text-white/80 text-[13px] font-semibold"
        style={{
          background: "rgba(20, 14, 30, 0.6)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(8px)",
        }}
      >
        <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} className="inline-block mr-1.5">✨</motion.span>
        Once they join, you&rsquo;ll both enter the room instantly
        <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="inline-block ml-1.5">✨</motion.span>
      </div>
    </div>
  );
}

function GameplayView({ room, role, playerName, refresh }) {
  const isMyTurn = room.turn === role;
  const [loadingNext, setLoadingNext] = useState(false);
  const [showScore, setShowScore] = useState(false);
  const triggeredRef = useRef(false);
  const fallbackTimerRef = useRef(null);

  // Self-healing auto-trigger for the next round.
  // - Host fires immediately when status='playing' && !current_content.
  // - Guest fires as a fallback ~3s later if host hasn't generated content
  //   (e.g. host tab disconnected, throttled, or out of focus). Either tab
  //   advancing /next is idempotent on the backend (it's a stateless
  //   generator keyed off room.turn), so the worst case is a single extra
  //   call which the polling cycle absorbs.
  useEffect(() => {
    // Clear any pending guest fallback every time deps change.
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    if (!room.current_content && room.status === "playing" && !triggeredRef.current) {
      if (role === "host") {
        triggeredRef.current = true;
        handleNext();
      } else {
        // Guest fallback: wait 3.2s, then if state still says
        // playing+empty, jump in and trigger generation.
        fallbackTimerRef.current = setTimeout(() => {
          if (!triggeredRef.current) {
            triggeredRef.current = true;
            handleNext();
          }
        }, 3200);
      }
    }
    if (room.current_content) {
      triggeredRef.current = false;
    }
    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.status, room.current_content, role]);

  const handleNext = async () => {
    setLoadingNext(true);
    try {
      await api.nextRound(room.code);
      refresh();
      refreshUsage();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.code === "daily_cap_reached") {
        openPaywall("daily_cap");
        refreshUsage();
      } else {
        // Silent on guest fallback path — host is probably already firing.
        // For host, surface the error.
        if (role === "host") toast.error("Generation failed");
      }
    } finally {
      setLoadingNext(false);
    }
  };

  const handleAction = async (action) => {
    try {
      await api.action(room.code, { player: role, action });
      refresh();
      refreshUsage();
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const handleBackToLobby = async () => {
    await api.backToLobby(room.code);
    refresh();
  };

  return (
    <div>
      {/* Scoreboard with pack badge */}
      <div className="relative mb-6">
        {(room.source === "pack" || room.source === "custom") && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
            {room.source === "pack" && room.pack_name && (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-[0.15em] backdrop-blur-md"
                style={{
                  background: `${room.pack_color || "#7B61FF"}26`,
                  border: `1px solid ${room.pack_color || "#7B61FF"}88`,
                  color: "#FFFFFF",
                  boxShadow: `0 0 18px ${room.pack_color || "#7B61FF"}55, inset 0 0 12px ${room.pack_color || "#7B61FF"}22`,
                }}
                data-testid="scoreboard-pack-badge"
              >
                <span className="text-sm">{room.pack_emoji}</span>
                <span>{room.pack_name}</span>
              </span>
            )}
            {room.source === "custom" && (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-[0.15em] backdrop-blur-md bg-[#00E5FF]/15 border border-[#00E5FF]/80 text-white"
                style={{ boxShadow: "0 0 18px #00E5FF55, inset 0 0 12px #00E5FF22" }}
                data-testid="scoreboard-custom-badge"
              >
                <PenLine className="w-3 h-3" />
                <span>Your questions</span>
              </span>
            )}
          </div>
        )}
        <div className="card-glow rounded-3xl p-5 flex items-center justify-between">
          <PlayerBadge name={room.host_name} score={room.score_host} active={room.turn === "host"} side="left" accent={room.source === "pack" ? room.pack_color : null} />
          <button
            type="button"
            onClick={() => setShowScore(true)}
            className="text-sm font-bold gradient-text px-3 py-1 rounded-full hover:bg-white/5 transition tracking-tight"
            data-testid="open-scorecard-button"
            aria-label="Share score"
          >
            VS
          </button>
          <PlayerBadge name={room.guest_name} score={room.score_guest} active={room.turn === "guest"} side="right" accent={room.source === "pack" ? room.pack_color : null} />
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 flex-wrap mb-6 text-xs">
        <span className="font-bold text-[#C9C7D9] uppercase tracking-[0.3em]">
          Round {room.round_number} · {room.mode.replaceAll("_", " ")} · {room.spice_level}
        </span>
      </div>

      {room.mode === "truth_or_dare" && (
        <TruthOrDareCard room={room} role={role} isMyTurn={isMyTurn} loading={loadingNext} onAction={handleAction} onNext={handleNext} />
      )}
      {room.mode === "quiz" && (
        <QuizCard room={room} role={role} loading={loadingNext} onNext={handleNext} refresh={refresh} />
      )}
      {room.mode === "date_night" && (
        <DateNightCard room={room} role={role} loading={loadingNext} onNext={handleNext} onAction={handleAction} isMyTurn={isMyTurn} />
      )}

      <div className="text-center mt-10">
        <button
          onClick={() => setShowScore(true)}
          className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white hover:bg-white/10 px-5 py-2.5 rounded-full text-sm font-bold mr-3 active:scale-95 transition"
          data-testid="share-score-button"
        >
          <Share2 className="w-4 h-4" />
          Share score
        </button>
        <button
          onClick={handleBackToLobby}
          className="text-sm font-semibold text-[#C9C7D9] hover:text-white underline underline-offset-4"
          data-testid="back-lobby-button"
        >
          End game & back to lobby
        </button>
      </div>

      <ScoreCard
        open={showScore}
        onClose={() => setShowScore(false)}
        hostName={room.host_name}
        guestName={room.guest_name}
        hostScore={room.score_host}
        guestScore={room.score_guest}
        roundsPlayed={room.round_number}
        modeLabel={(room.mode || "").replaceAll("_", " ")}
        packEmoji={room.pack_emoji}
        packName={room.pack_name}
        spiceLevel={room.spice_level}
      />
    </div>
  );
}

function PlayerBadge({ name, score, active, side, accent }) {
  const activeStyle = active
    ? accent
      ? {
          background: accent,
          boxShadow: `0 0 24px ${accent}99, 0 0 42px ${accent}44`,
        }
      : {
          background: "linear-gradient(135deg, #FF4D8D 0%, #7B61FF 100%)",
          boxShadow: "0 0 24px rgba(255, 77, 141, 0.6)",
        }
    : undefined;
  const scoreColor = accent || "#00E5FF";
  return (
    <div className={`flex items-center gap-3 ${side === "right" ? "flex-row-reverse" : ""}`}>
      <div
        className={`w-12 h-12 rounded-2xl flex items-center justify-center font-display-tight font-bold text-xl transition ${
          active
            ? "text-white scale-110"
            : "bg-white/5 text-[#C9C7D9] border border-white/10"
        }`}
        style={activeStyle}
      >
        {(name || "?").charAt(0).toUpperCase()}
      </div>
      <div className={side === "right" ? "text-right" : ""}>
        <p className="font-semibold text-white text-sm leading-tight">{name}</p>
        <p className="text-xs text-[#C9C7D9]">Score: <span className="font-bold" style={{ color: scoreColor }}>{score}</span></p>
      </div>
    </div>
  );
}

function TruthOrDareCard({ room, role, isMyTurn, loading, onAction, onNext }) {
  const content = room.current_content;
  const { openPaywall, isPremium } = useBilling();
  const [showDare, setShowDare] = useState(false);
  const [mode, setInputMode] = useState(null); // null | 'type' | 'voice' | 'video'
  const [typedText, setTypedText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [videoUsage, setVideoUsage] = useState(null);

  // Refresh the user's daily video quota whenever the dare view is shown
  useEffect(() => {
    if (!showDare || isPremium) return;
    let alive = true;
    api.usageVideoToday().then((u) => { if (alive) setVideoUsage(u); }).catch(() => {});
    return () => { alive = false; };
  }, [showDare, isPremium, room.round_number, room.status]);

  const typing = mode === "type";
  const recording = mode === "voice";
  const videoing = mode === "video";
  const typedReveal = room.status === "answer_revealed" && room.typed_answer;

  if (!content && !typedReveal) {
    return (
      <CardSkeleton>
        <button
          onClick={onNext}
          disabled={loading}
          className="btn-gradient px-9 py-4 rounded-full font-bold disabled:opacity-50 active:scale-95"
          data-testid="start-round-button"
        >
          {loading ? "Conjuring..." : "Start round"}
        </button>
      </CardSkeleton>
    );
  }

  const label = showDare ? "Dare" : "Truth";
  const color = showDare ? "#7B61FF" : "#FF4D8D";

  // Reveal view — partner sees the typed, voice, or video answer
  if (typedReveal) {
    const typerName = room.typed_answer.player === "host" ? room.host_name : room.guest_name;
    const isMyAnswer = room.typed_answer.player === role;
    const isAudio = room.typed_answer.type === "audio";
    const isVideo = room.typed_answer.type === "video";
    return (
      <TruthRevealCard
        room={room}
        content={content}
        typerName={typerName}
        isMyAnswer={isMyAnswer}
        isAudio={isAudio}
        isVideo={isVideo}
        role={role}
        onAction={onAction}
      />
    );
  }

  const handleSubmitTyped = async () => {
    if (!typedText.trim()) return toast.error("Write something first");
    setSubmitting(true);
    try {
      await api.submitAnswer(room.code, { player: role, answer: typedText.trim(), answer_type: "text" });
      setTypedText("");
      setInputMode(null);
    } catch (e) {
      toast.error("Couldn't send");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitVoice = async (base64DataUrl) => {
    setSubmitting(true);
    try {
      await api.submitAnswer(room.code, { player: role, answer: base64DataUrl, answer_type: "audio" });
      setInputMode(null);
    } catch (e) {
      toast.error("Couldn't send voice note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitVideo = async (base64DataUrl) => {
    setSubmitting(true);
    try {
      await api.submitAnswer(room.code, { player: role, answer: base64DataUrl, answer_type: "video" });
      setInputMode(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && detail?.code === "video_cap_reached") {
        openPaywall("video");
        setInputMode(null);
      } else if (status === 413) {
        toast.error("Video too big — try a shorter clip.");
      } else {
        toast.error("Couldn't send video");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVideoTap = async () => {
    if (isPremium) {
      setInputMode("video");
      return;
    }
    try {
      const u = await api.usageVideoToday();
      if ((u?.remaining ?? 0) <= 0) {
        openPaywall("video");
        return;
      }
    } catch { /* fall through and let backend gate */ }
    setInputMode("video");
  };

  return (
    <SwipeableCard
      key={room.round_number}
      enabled={isMyTurn && !typing && !videoing}
      onSwipe={(dir) => {
        if (dir === "right") { toast.success("Green flag 💚"); onAction("accept"); }
        else if (dir === "left") { toast("Red flag 🚩", { style: { background: "rgba(40,20,24,0.95)", border: "1px solid rgba(255,77,141,0.35)", color: "#FFFFFF" } }); onAction("skip"); }
        else if (dir === "up") { toast.success("That's deep 😳"); onAction("accept"); }
      }}
    >
      <div
        className="relative rounded-[28px] p-7 md:p-10 overflow-hidden"
        style={{
          background:
            `radial-gradient(ellipse at 50% 0%, ${color}30 0%, rgba(12, 6, 18, 0.85) 75%)`,
          border: `1.5px solid ${color}`,
          boxShadow:
            `0 0 48px ${color}aa, 0 0 90px ${color}50, inset 0 0 38px ${color}1f, inset 0 1px 0 rgba(255, 255, 255, 0.08)`,
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
        }}
      >
        {/* Drifting inner glow */}
        <motion.div
          className="absolute pointer-events-none"
          animate={{
            x: ["-15%", "15%", "-15%"],
            y: ["-10%", "10%", "-10%"],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{
            left: "30%", top: "10%",
            width: "60%", height: "60%",
            background: `radial-gradient(circle, ${color}55 0%, transparent 65%)`,
            filter: "blur(28px)",
            borderRadius: "50%",
          }}
          aria-hidden
        />
        <div className="text-center mb-6 relative">
          <span
            className="inline-block text-xs font-bold uppercase tracking-[0.3em] px-5 py-2 rounded-full text-white"
            style={{
              background: `${color}22`,
              border: `1px solid ${color}55`,
              boxShadow: `0 0 20px ${color}33`,
              color,
            }}
          >
            {label} · For {isMyTurn ? "you" : (room.turn === "host" ? room.host_name : room.guest_name)}
          </span>
        </div>
        <motion.p
          key={showDare ? "dare" : "truth"}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center leading-[1.25] mb-8 min-h-[120px] flex items-center justify-center relative"
          style={{ textShadow: "0 2px 16px rgba(0, 0, 0, 0.45)" }}
          data-testid="current-content-text"
        >
          {showDare ? content.dare : content.truth}
        </motion.p>

        {/* Pack emoji bottom accent — matches Together style */}
        {room.pack_emoji && (
          <motion.div
            animate={{ scale: [1, 1.08, 1], rotate: [-3, 3, -3] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="text-[40px] text-center mb-4 relative"
            style={{ filter: `drop-shadow(0 0 14px ${color})` }}
          >
            {room.pack_emoji}
          </motion.div>
        )}

        {!typing && (
          <div className="flex justify-center mb-8">
            <button
              onClick={() => setShowDare((s) => !s)}
              className="text-sm font-semibold text-[#00E5FF] hover:underline underline-offset-4"
              data-testid="toggle-truth-dare-button"
            >
              Switch to {showDare ? "Truth" : "Dare"}
            </button>
          </div>
        )}

        {isMyTurn ? (
          typing ? (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <label className="block text-xs font-bold text-[#C9C7D9] mb-2 ml-1 uppercase tracking-[0.2em]">
                Your typed answer
              </label>
              <textarea
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                placeholder="Type it out…"
                rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-[#C9C7D9]/50 focus:outline-none focus:ring-2 focus:ring-[#FF4D8D]/60 resize-none mb-4"
                data-testid="typed-answer-input"
                autoFocus
              />
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleSubmitTyped}
                  disabled={submitting}
                  className="btn-gradient rounded-full py-4 font-bold active:scale-95 disabled:opacity-60 transition"
                  data-testid="send-typed-button"
                >
                  {submitting ? "Sending..." : "Your turn 👀"}
                </button>
                <button
                  onClick={() => { setInputMode(null); setTypedText(""); }}
                  className="bg-white/5 border border-white/10 text-white rounded-full py-4 font-bold hover:bg-white/10 transition"
                  data-testid="cancel-typed-button"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          ) : recording ? (
            <VoiceRecorder
              sending={submitting}
              onSend={handleSubmitVoice}
              onCancel={() => setInputMode(null)}
            />
          ) : videoing ? (
            <VideoRecorder
              sending={submitting}
              onSend={handleSubmitVideo}
              onCancel={() => setInputMode(null)}
            />
          ) : (
            <div className="space-y-3">
              {!showDare && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setInputMode("type")}
                    className="bg-white/5 border border-[#FF4D8D]/40 text-[#FF4D8D] rounded-full py-3.5 font-bold hover:bg-[#FF4D8D]/10 hover:border-[#FF4D8D]/80 active:scale-95 transition flex items-center justify-center gap-2"
                    data-testid="type-answer-button"
                  >
                    <PenLine className="w-4 h-4" />
                    Type
                  </button>
                  <button
                    onClick={() => setInputMode("voice")}
                    className="bg-white/5 border border-[#7B61FF]/40 text-[#7B61FF] rounded-full py-3.5 font-bold hover:bg-[#7B61FF]/10 hover:border-[#7B61FF]/80 active:scale-95 transition flex items-center justify-center gap-2"
                    data-testid="voice-answer-button"
                  >
                    <Mic className="w-4 h-4" />
                    Voice note
                  </button>
                </div>
              )}
              {showDare && (
                <button
                  type="button"
                  onClick={handleVideoTap}
                  className="relative w-full bg-gradient-to-br from-[#FF4D8D]/15 to-[#7B61FF]/15 border border-[#FF4D8D]/50 text-white rounded-full py-3.5 font-bold hover:border-[#FF4D8D] active:scale-95 transition inline-flex items-center justify-center gap-2 overflow-hidden"
                  data-testid="video-answer-button"
                >
                  <Video className="w-4 h-4 text-[#FF4D8D]" />
                  Prove it 👀
                  <span
                    className="ml-1 text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full"
                    style={{ background: "#FF4D8D22", color: "#FF4D8D", border: "1px solid #FF4D8D55" }}
                    data-testid="video-answer-badge"
                  >
                    {isPremium
                      ? "Premium"
                      : videoUsage && videoUsage.remaining <= 0
                      ? "Used today 🔒"
                      : "1 free video today"}
                  </span>
                </button>
              )}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => onAction("accept")}
                  className="btn-gradient rounded-full py-4 font-bold active:scale-95 transition"
                  data-testid="accept-button"
                >
                  Your turn 👀
                </button>
                <button
                  onClick={() => onAction("skip")}
                  className="bg-white/5 border border-white/10 text-white rounded-full py-4 font-bold hover:bg-white/10 active:scale-95 transition"
                  data-testid="skip-button"
                >
                  Skip
                </button>
              </div>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center gap-3 relative" data-testid="remotely-question-waiting">
            <div
              className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full"
              style={{
                background: "rgba(20, 14, 26, 0.6)",
                border: `1.5px solid ${color}88`,
                boxShadow: `0 0 16px ${color}55, inset 0 1px 0 rgba(255, 255, 255, 0.06)`,
                backdropFilter: "blur(10px)",
              }}
            >
              <span className="text-white text-[13px] font-extrabold">
                They're answering
              </span>
              <span className="inline-flex gap-0.5">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    animate={{ opacity: [0.2, 1, 0.2], y: [0, -2, 0] }}
                    transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.18 }}
                    className="text-white text-[16px] leading-none"
                  >
                    •
                  </motion.span>
                ))}
              </span>
            </div>
            <p className="text-white/55 text-[12px] font-medium">
              You'll get notified the moment they're done 💜
            </p>
          </div>
        )}
      </div>
    </SwipeableCard>
  );
}

function SwipeableCard({ children, enabled, onSwipe }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-240, 0, 240], [-14, 0, 14]);
  const rightOpacity = useTransform(x, [40, 160], [0, 1]);
  const leftOpacity = useTransform(x, [-160, -40], [1, 0]);
  const upOpacity = useTransform(y, [-160, -40], [1, 0]);

  const handleDragEnd = (_e, info) => {
    const { offset, velocity } = info;
    const vx = velocity.x;
    const vy = velocity.y;
    const vibe = (ms) => { try { navigator.vibrate?.(ms); } catch (_e) { /* noop */ } };
    if (offset.x > 120 || vx > 600) { vibe([12, 30, 12]); onSwipe("right"); }
    else if (offset.x < -120 || vx < -600) { vibe(18); onSwipe("left"); }
    else if (offset.y < -120 || vy < -600) { vibe([8, 20, 8, 20, 8]); onSwipe("up"); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      drag={enabled}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.8}
      onDragEnd={handleDragEnd}
      style={{ x, y, rotate, touchAction: enabled ? "none" : "auto" }}
      whileDrag={{ cursor: "grabbing" }}
      className={`max-w-2xl mx-auto relative ${enabled ? "cursor-grab" : ""}`}
      data-testid="swipeable-card"
    >
      {children}
      {enabled && (
        <>
          <motion.div
            style={{ opacity: rightOpacity }}
            className="absolute inset-0 rounded-3xl pointer-events-none flex items-center justify-center"
          >
            <div className="absolute inset-0 rounded-3xl" style={{ background: "radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.25), transparent 70%)", border: "2px solid rgba(16, 185, 129, 0.6)" }} />
            <span className="relative z-10 font-display text-4xl md:text-5xl font-bold text-[#10B981] rotate-12 select-none" style={{ textShadow: "0 0 30px rgba(16, 185, 129, 0.6)" }}>
              💚 Green flag
            </span>
          </motion.div>
          <motion.div
            style={{ opacity: leftOpacity }}
            className="absolute inset-0 rounded-3xl pointer-events-none flex items-center justify-center"
          >
            <div className="absolute inset-0 rounded-3xl" style={{ background: "radial-gradient(circle at 50% 50%, rgba(255, 77, 141, 0.25), transparent 70%)", border: "2px solid rgba(255, 77, 141, 0.6)" }} />
            <span className="relative z-10 font-display text-4xl md:text-5xl font-bold text-[#FF4D8D] -rotate-12 select-none" style={{ textShadow: "0 0 30px rgba(255, 77, 141, 0.6)" }}>
              🚩 Red flag
            </span>
          </motion.div>
          <motion.div
            style={{ opacity: upOpacity }}
            className="absolute inset-0 rounded-3xl pointer-events-none flex items-center justify-center"
          >
            <div className="absolute inset-0 rounded-3xl" style={{ background: "radial-gradient(circle at 50% 50%, rgba(123, 97, 255, 0.25), transparent 70%)", border: "2px solid rgba(123, 97, 255, 0.6)" }} />
            <span className="relative z-10 font-display text-3xl md:text-4xl font-bold text-[#7B61FF] select-none" style={{ textShadow: "0 0 30px rgba(123, 97, 255, 0.6)" }}>
              😳 That's deep
            </span>
          </motion.div>
          <div className="absolute -bottom-8 left-0 right-0 text-center text-xs text-[#C9C7D9]/60 font-semibold uppercase tracking-wider pointer-events-none">
            ← skip · swipe up: deep · accept →
          </div>
        </>
      )}
    </motion.div>
  );
}

function QuizCard({ room, role, loading, onNext, refresh }) {
  const [answer, setAnswer] = useState("");
  const myAnswer = room.quiz_answers?.[role];
  const content = room.current_content;

  if (!content) {
    return (
      <CardSkeleton>
        {role === "host" ? (
          <button
            onClick={onNext}
            disabled={loading}
            className="btn-gradient px-9 py-4 rounded-full font-bold disabled:opacity-50 active:scale-95"
            data-testid="start-round-button"
          >
            {loading ? "Thinking..." : "New question"}
          </button>
        ) : (
          <p className="text-[#C9C7D9] font-semibold">Waiting for the host…</p>
        )}
      </CardSkeleton>
    );
  }

  const submit = async () => {
    if (!answer.trim()) return toast.error("Write an answer");
    await api.quizAnswer(room.code, { player: role, answer: answer.trim() });
    setAnswer("");
    refresh();
  };

  const continueNext = async (matched) => {
    await api.quizContinue(room.code, matched);
    setAnswer("");
    refresh();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="card-glow rounded-3xl p-8 md:p-12">
        <span className="block text-xs font-bold uppercase tracking-[0.3em] text-[#7B61FF] text-center mb-5">
          Partner Quiz
        </span>
        <p className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center leading-[1.25] mb-10" data-testid="quiz-question-text">
          {content.question}
        </p>

        {room.status === "quiz_answering" && (
          <>
            {myAnswer ? (
              <div className="text-center py-6">
                <div className="inline-flex items-center gap-2 bg-[#00E5FF]/10 text-[#00E5FF] px-4 py-2 rounded-full font-semibold mb-3 border border-[#00E5FF]/30">
                  <Check className="w-4 h-4" /> You answered
                </div>
                <p className="text-[#C9C7D9]">Waiting for your partner…</p>
              </div>
            ) : (
              <>
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Your guess..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-[#C9C7D9]/50 focus:outline-none focus:ring-2 focus:ring-[#7B61FF]/60 mb-5 resize-none"
                  data-testid="quiz-answer-input"
                />
                <button
                  onClick={submit}
                  className="w-full btn-gradient rounded-full py-4 font-bold active:scale-95 transition"
                  data-testid="quiz-submit-button"
                >
                  Submit answer
                </button>
              </>
            )}
          </>
        )}

        {room.status === "quiz_reveal" && (
          <div>
            <div className="grid sm:grid-cols-2 gap-3 mb-7">
              <AnswerReveal label={room.host_name} text={room.quiz_answers?.host} color="#FF4D8D" />
              <AnswerReveal label={room.guest_name} text={room.quiz_answers?.guest} color="#00E5FF" />
            </div>
            {role === "host" && (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => continueNext(true)}
                  className="btn-gradient rounded-full py-4 font-bold active:scale-95"
                  data-testid="quiz-match-button"
                >
                  We matched! +1 each
                </button>
                <button
                  onClick={() => continueNext(false)}
                  className="bg-white/5 border border-white/10 text-white rounded-full py-4 font-bold hover:bg-white/10 active:scale-95"
                  data-testid="quiz-no-match-button"
                >
                  Didn't match
                </button>
              </div>
            )}
            {role !== "host" && (
              <p className="text-center text-[#C9C7D9] font-semibold">Host is scoring the round…</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AnswerReveal({ label, text, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-5"
      style={{
        background: `${color}11`,
        border: `1px solid ${color}44`,
        boxShadow: `0 0 24px ${color}1a`,
      }}
    >
      <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2" style={{ color }}>
        {label}
      </p>
      <p className="font-semibold text-white">{text || "—"}</p>
    </motion.div>
  );
}

function DateNightCard({ room, role, loading, onNext, onAction, isMyTurn }) {
  const content = room.current_content;
  if (!content) {
    return (
      <CardSkeleton>
        {role === "host" ? (
          <button
            onClick={onNext}
            disabled={loading}
            className="btn-gradient px-9 py-4 rounded-full font-bold disabled:opacity-50 active:scale-95"
            data-testid="spin-date-button"
          >
            {loading ? "Spinning..." : "Spin for a date idea"}
          </button>
        ) : (
          <p className="text-[#C9C7D9] font-semibold">Host is spinning…</p>
        )}
      </CardSkeleton>
    );
  }

  return (
    <motion.div
      key={room.round_number}
      initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ type: "spring", stiffness: 120 }}
      className="max-w-2xl mx-auto"
    >
      <div className="relative rounded-3xl p-9 md:p-12 overflow-hidden" style={{
        background: "linear-gradient(135deg, #FF4D8D 0%, #7B61FF 60%, #00E5FF 120%)",
        boxShadow: "0 20px 80px rgba(123, 97, 255, 0.5)",
      }}>
        <span className="inline-block text-xs font-bold uppercase tracking-[0.3em] bg-white/20 backdrop-blur text-white px-3 py-1.5 rounded-full mb-5">
          {content.category || "Date"}
        </span>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-5 leading-[1.1]" data-testid="date-title">
          {content.title}
        </h2>
        <p className="text-white/95 text-lg leading-relaxed">{content.description}</p>
      </div>
      {role === "host" && (
        <div className="grid grid-cols-2 gap-3 mt-6">
          <button
            onClick={() => onAction("accept")}
            className="btn-gradient rounded-full py-4 font-bold active:scale-95"
            data-testid="date-love-button"
          >
            Love it 💕
          </button>
          <button
            onClick={() => onAction("skip")}
            className="bg-white/5 border border-white/10 text-white rounded-full py-4 font-bold hover:bg-white/10 active:scale-95"
            data-testid="date-skip-button"
          >
            Skip & spin again
          </button>
        </div>
      )}
    </motion.div>
  );
}

function CardSkeleton({ children }) {
  return (
    <div className="max-w-2xl mx-auto card-glow rounded-3xl p-14 text-center min-h-[340px] flex flex-col items-center justify-center gap-5">
      <Sparkles className="w-12 h-12 text-[#00E5FF]" fill="#00E5FF" />
      {children}
    </div>
  );
}

function TruthRevealCard({ room, content, typerName, isMyAnswer, isAudio, isVideo, role, onAction }) {
  const [audioSrc, setAudioSrc] = useState(null);
  const [audioErr, setAudioErr] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const [videoErr, setVideoErr] = useState(false);
  const [burst, setBurst] = useState(false);
  const [reactionBurst, setReactionBurst] = useState(false);
  const [reactionDraft, setReactionDraft] = useState("");
  const [reactionSending, setReactionSending] = useState(false);
  const reactionInputRef = useRef(null);
  const audioId = room.typed_answer?.audio_id;
  const videoId = room.typed_answer?.video_id;
  const reaction = room.typed_answer?.reaction;
  const myName = role === "host" ? room.host_name : room.guest_name;

  useEffect(() => {
    let cancelled = false;
    if (isAudio && audioId) {
      setAudioSrc(null);
      setAudioErr(false);
      api.getAudio(audioId)
        .then((r) => { if (!cancelled) setAudioSrc(r.data); })
        .catch(() => { if (!cancelled) setAudioErr(true); });
    }
    if (isVideo && videoId) {
      setVideoSrc(null);
      setVideoErr(false);
      api.getVideo(videoId)
        .then((r) => { if (!cancelled) setVideoSrc(r.data); })
        .catch(() => { if (!cancelled) setVideoErr(true); });
    }
    return () => { cancelled = true; };
  }, [isAudio, audioId, isVideo, videoId]);

  const sendReaction = async (overrideText) => {
    const text = (overrideText ?? reactionDraft).trim();
    if (!text || reactionSending) return;
    setReactionSending(true);
    try {
      await api.reactToAnswer(room.code, { player: role, text });
      setReactionDraft("");
      // Mini burst on successful reaction — fades after ~900ms
      setReactionBurst(true);
      setTimeout(() => setReactionBurst(false), 950);
      // Haptic feedback on devices that support it (graceful no-op elsewhere)
      try { navigator.vibrate?.(18); } catch { /* noop */ }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't send reaction";
      toast.error(typeof msg === "string" ? msg : "Couldn't send reaction");
    } finally {
      setReactionSending(false);
    }
  };

  // Auto-focus the reaction input after the reveal animation finishes —
  // only on the receiving side and only when no reaction has been sent yet.
  useEffect(() => {
    if (isMyAnswer || reaction) return;
    const t = setTimeout(() => {
      try { reactionInputRef.current?.focus({ preventScroll: true }); } catch { /* noop */ }
    }, 700);
    return () => clearTimeout(t);
  }, [isMyAnswer, reaction]);

  const QUICK_EMOJI = ["😍", "💜", "🔥", "😳"];

  const promptText = isVideo ? content?.dare : content?.truth;
  const packName = (room.pack_name || (isVideo ? "Dare" : "Truth")).toUpperCase();
  const showReactionInput = !isMyAnswer && !reaction;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      onAnimationComplete={() => setBurst(true)}
      className="max-w-xl mx-auto relative"
      data-testid="reveal-thread"
    >
      {burst && (
        <div className="absolute inset-0 pointer-events-none z-20">
          <ConfettiBurst count={16} />
        </div>
      )}

      {/* Pack + round header */}
      <div className="text-center mb-4">
        <span
          className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] px-4 py-1.5 rounded-full"
          style={{
            background: "rgba(255,77,141,0.08)",
            border: "1px solid rgba(255,77,141,0.35)",
            boxShadow: "0 0 18px rgba(255,77,141,0.18)",
            color: "#FF4D8D",
          }}
          data-testid="reveal-pack-badge"
        >
          {isAudio ? <Mic className="w-3 h-3" /> : isVideo ? <Video className="w-3 h-3" /> : <Flame className="w-3 h-3" />}
          {packName}
        </span>
        <div className="text-xs text-white/40 mt-1.5 tracking-wide">
          ROUND {room.round_number}/{room.total_rounds || 10}
        </div>
      </div>

      {/* Partner avatar with glow ring — "Their answer 💜" */}
      {!isMyAnswer && (() => {
        // Find the partner's cinematic avatar (stored by SetupView in localStorage)
        const partnerAvatarId =
          role === "host" ? room.guest_avatar_id : room.host_avatar_id;
        const AVATARS = [
          { id: "pink-w",   img: "/assets/home/avatar-pink-w.png",   color: "#FF2E9A" },
          { id: "purple-w", img: "/assets/home/avatar-purple-w.png", color: "#9B6BFF" },
          { id: "orange-w", img: "/assets/home/avatar-orange-w.png", color: "#FFB547" },
          { id: "red-m",    img: "/assets/home/avatar-red-m.png",    color: "#FF3D5A" },
          { id: "blue-m",   img: "/assets/home/avatar-blue-m.png",   color: "#4FC3FF" },
          { id: "green-m",  img: "/assets/home/avatar-green-m.png",  color: "#3DD68C" },
        ];
        const av = AVATARS.find((a) => a.id === partnerAvatarId);
        const ringColor = av?.color || "#9B6BFF";
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center mb-5"
            data-testid="reveal-partner-avatar"
          >
            <div className="relative">
              <motion.div
                animate={{ scale: [1, 1.18, 1], opacity: [0.5, 0.9, 0.5] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-[-10px] rounded-full pointer-events-none"
                style={{
                  background: `radial-gradient(circle, ${ringColor}b0 0%, transparent 70%)`,
                  filter: "blur(8px)",
                }}
                aria-hidden
              />
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
                className="absolute inset-[-4px] rounded-full pointer-events-none"
                style={{
                  background: `conic-gradient(from 0deg, transparent, ${ringColor}, transparent 50%, #FF2E9A, transparent)`,
                  filter: "blur(3px)",
                  opacity: 0.7,
                }}
                aria-hidden
              />
              <div
                className="relative w-[72px] h-[72px] rounded-full overflow-hidden flex items-center justify-center"
                style={{
                  background: av ? "#0a060f" : "linear-gradient(135deg, #C9B7FF, #9B6BFF 60%, #FF2E9A)",
                  border: "2.5px solid rgba(255, 255, 255, 0.22)",
                  boxShadow: `0 0 24px ${ringColor}d0, inset 0 0 14px rgba(0, 0, 0, 0.35)`,
                }}
              >
                {av ? (
                  <img src={av.img} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-extrabold text-[28px]">
                    {(typerName || "?")[0]?.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            <div
              className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full"
              style={{
                background: "rgba(20, 14, 26, 0.55)",
                border: `1px solid ${ringColor}88`,
                boxShadow: `0 0 12px ${ringColor}55`,
              }}
            >
              <span className="text-white text-[12px] font-extrabold tracking-wide">
                {typerName}'s answer
              </span>
              <span style={{ filter: `drop-shadow(0 0 5px ${ringColor})` }}>💜</span>
            </div>
          </motion.div>
        );
      })()}

      {/* Prompt card — universal vc-q system (spicy tone) */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.05, duration: 0.4 }}
        className="vc-q-card vc-q-spicy vc-q-active mb-5"
        style={{ padding: "26px 20px 22px" }}
      >
        <p className="vc-q-text" style={{ fontSize: "clamp(20px, 4.8vw, 26px)" }}>
          {promptText}
        </p>
        {!isMyAnswer && (
          <div className="text-[#FF4D8D] text-xs font-semibold mt-3 flex items-center gap-1.5" data-testid="reveal-they-answered">
            <span>they answered</span>
            <motion.span
              animate={{ scale: [1, 1.25, 1], rotate: [0, -8, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.4 }}
              style={{ display: "inline-block", transformOrigin: "center" }}
              aria-hidden
            >
              👀
            </motion.span>
          </div>
        )}
        {isMyAnswer && (
          <div className="text-[#FF4D8D]/70 text-xs font-semibold mt-3 flex items-center gap-1.5">
            <span>you answered</span>
            <span>✨</span>
          </div>
        )}
      </motion.div>

      {/* Partner's answer bubble (LEFT, dark) */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.18, duration: 0.4 }}
        className="mb-2"
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5 ml-3 font-semibold">
          {typerName}
        </div>
        <div
          className="inline-block max-w-[88%] rounded-3xl rounded-bl-md px-5 py-3.5"
          style={{
            background: "linear-gradient(135deg, rgba(40,30,55,0.95), rgba(28,18,38,0.95))",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          }}
        >
          {isAudio ? (
            audioErr ? (
              <p className="text-white/70 text-sm" data-testid="reveal-audio-error">Couldn't load voice note.</p>
            ) : audioSrc ? (
              <audio
                controls
                autoPlay
                src={audioSrc}
                className="max-w-full"
                style={{ minWidth: "240px" }}
                data-testid="reveal-audio-player"
              />
            ) : (
              <Loader2 className="w-5 h-5 animate-spin text-[#FF4D8D]" />
            )
          ) : isVideo ? (
            <div className="min-w-[260px]">
              <VideoOnceReveal
                videoSrc={videoSrc}
                videoErr={videoErr}
                videoId={videoId}
                typerName={typerName}
                roundNumber={room.round_number}
              />
            </div>
          ) : (
            <p className="text-white text-base sm:text-lg leading-relaxed" data-testid="typed-answer-reveal">
              {room.typed_answer.answer}
            </p>
          )}
        </div>
      </motion.div>

      {/* Reaction bubble (RIGHT, pink) — your reply */}
      {!isMyAnswer && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.32, duration: 0.4 }}
          className="text-right mt-4 mb-1 relative"
        >
          {reactionBurst && (
            <div className="absolute top-0 right-0 pointer-events-none z-30" data-testid="reveal-reaction-burst">
              <ConfettiBurst count={10} />
            </div>
          )}
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5 mr-3 font-semibold">
            {myName || "you"}
          </div>
          {reaction ? (
            <>
              <div
                className="inline-block max-w-[88%] rounded-3xl rounded-br-md px-5 py-3"
                style={{
                  background: "linear-gradient(135deg, #FF4D8D, #E63A78)",
                  boxShadow: "0 4px 22px rgba(255,77,141,0.45)",
                }}
                data-testid="reveal-reaction-text"
              >
                <p className="text-white text-base leading-relaxed">{reaction.text}</p>
              </div>
              <div className="text-[10px] text-white/40 mt-1 mr-3 flex items-center justify-end gap-1">
                <span>seen</span>
                <Check className="w-3 h-3 -mr-1" />
                <Check className="w-3 h-3" />
              </div>
            </>
          ) : showReactionInput ? (
            <div className="flex flex-col items-end gap-2">
              {/* Quick-emoji tap row — one-tap reactions for the 2am crowd */}
              <div className="flex items-center gap-1.5" data-testid="reveal-quick-emoji">
                {QUICK_EMOJI.map((e) => (
                  <button
                    key={e}
                    onClick={() => sendReaction(e)}
                    disabled={reactionSending}
                    className="w-9 h-9 rounded-full flex items-center justify-center text-lg transition active:scale-90 hover:scale-110 disabled:opacity-50"
                    style={{
                      background: "rgba(255,77,141,0.10)",
                      border: "1px solid rgba(255,77,141,0.30)",
                    }}
                    data-testid={`reveal-quick-emoji-${e}`}
                    aria-label={`React with ${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
              <div
                className="inline-flex items-center gap-2 max-w-[88%] rounded-full pl-4 pr-1.5 py-1.5"
                style={{
                  background: "rgba(255,77,141,0.12)",
                  border: "1.5px solid rgba(255,77,141,0.45)",
                  boxShadow: "0 0 18px rgba(255,77,141,0.18)",
                }}
              >
                <input
                  ref={reactionInputRef}
                  type="text"
                  value={reactionDraft}
                  onChange={(e) => setReactionDraft(e.target.value.slice(0, 280))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      sendReaction();
                    }
                  }}
                  placeholder="say something back…"
                  className="bg-transparent outline-none text-white text-sm placeholder-white/40 w-[200px] sm:w-[260px]"
                  data-testid="reveal-reaction-input"
                  disabled={reactionSending}
                />
                <button
                  onClick={() => sendReaction()}
                  disabled={!reactionDraft.trim() || reactionSending}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-r from-[#FF4D8D] to-[#7B61FF] text-white disabled:opacity-40 active:scale-95 transition shadow-[0_0_16px_rgba(255,77,141,0.5)]"
                  data-testid="reveal-reaction-send"
                  aria-label="Send reaction"
                >
                  {reactionSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4 fill-white" />}
                </button>
              </div>
            </div>
          ) : null}
        </motion.div>
      )}

      {/* Next-round CTA */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45, duration: 0.4 }}
        className="mt-7"
      >
        <button
          onClick={() => onAction("accept")}
          className="btn-gradient w-full rounded-full py-4 font-bold active:scale-95 transition"
          data-testid="reveal-continue-button"
        >
          {isMyAnswer ? "Next round →" : reaction ? "Next round 🔥" : "Skip & next round →"}
        </button>
      </motion.div>
    </motion.div>
  );
}

function VideoOnceReveal({ videoSrc, videoErr, videoId, typerName, roundNumber }) {
  const [stage, setStage] = useState("gate"); // 'gate' | 'playing' | 'gone' | 'error'
  const [consumed, setConsumed] = useState(false);
  const vidRef = useRef(null);

  useEffect(() => {
    if (videoErr) setStage("error");
  }, [videoErr]);

  const consume = useCallback(async () => {
    if (consumed || !videoId) return;
    setConsumed(true);
    try { await api.consumeVideo(videoId); } catch { /* idempotent */ }
  }, [consumed, videoId]);

  // Privacy nudge — flag visibility changes + PrintScreen keypress while playing
  useEffect(() => {
    if (stage !== "playing") return;
    const onVis = () => {
      if (document.hidden) {
        toast.message("Eyes only 👀", { description: "Videos are view-once. No screenshots please." });
      }
    };
    const onKey = (e) => {
      if (e.key === "PrintScreen" || (e.shiftKey && e.metaKey && (e.key === "3" || e.key === "4"))) {
        toast.message("Respect the vibe 💕", { description: "This one's just between you two." });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      document.removeEventListener("keydown", onKey);
    };
  }, [stage]);

  const play = async () => {
    setStage("playing");
    await new Promise((r) => setTimeout(r, 50));
    try {
      await vidRef.current?.play();
    } catch { /* user-gesture already counted */ }
  };

  if (stage === "error") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 min-h-[200px] flex items-center justify-center"
      >
        <p className="text-[#C9C7D9] text-sm" data-testid="reveal-video-error">
          Video's gone 💨 (one-time view)
        </p>
      </motion.div>
    );
  }

  if (stage === "gone") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mb-8 min-h-[200px] flex flex-col items-center justify-center gap-3"
        data-testid="reveal-video-ended"
      >
        <motion.span
          className="text-4xl"
          animate={{ y: [-4, 4, -4], opacity: [1, 0.6, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        >
          💨
        </motion.span>
        <p className="text-lg font-bold text-white tracking-tight">This one's gone</p>
        <p className="text-xs text-[#C9C7D9] text-center max-w-xs">
          Videos are view-once — no replays, no saves. Just a moment between you two.
        </p>
      </motion.div>
    );
  }

  if (stage === "gate") {
    return (
      <motion.button
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        whileTap={{ scale: 0.97 }}
        onClick={videoSrc ? play : undefined}
        disabled={!videoSrc}
        className="relative w-full aspect-[4/5] max-w-md mx-auto mb-8 rounded-3xl overflow-hidden border border-white/10 group"
        style={{
          background:
            "radial-gradient(circle at 30% 20%, rgba(255,77,141,0.35), rgba(15,15,20,0.95) 60%)",
        }}
        data-testid="reveal-video-gate"
      >
        {/* Noise */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 1px, transparent 4px)",
          }}
        />
        <motion.div
          aria-hidden
          className="absolute inset-0"
          animate={{ backgroundPositionX: ["0%", "200%"] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,77,141,0.18) 50%, transparent 100%)",
            backgroundSize: "50% 100%",
            backgroundRepeat: "no-repeat",
          }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center">
          {!videoSrc ? (
            <Loader2 className="w-8 h-8 animate-spin text-[#FF4D8D]" />
          ) : (
            <>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.3em] px-3 py-1 rounded-full text-[#FF4D8D] border border-[#FF4D8D]/50 bg-[#FF4D8D]/10">
                <Lock className="w-3 h-3" /> View once
              </span>
              <motion.div
                className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FF4D8D] to-[#7B61FF] flex items-center justify-center glow-pink"
                animate={{ scale: [1, 1.06, 1] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              >
                <Video className="w-9 h-9 text-white" />
              </motion.div>
              <p className="text-lg font-bold text-white tracking-tight">
                {typerName} sent a video
              </p>
              <p className="text-xs text-[#C9C7D9] leading-relaxed">
                Tap to play · deletes when it ends · no screenshots please 💕
              </p>
            </>
          )}
        </div>
      </motion.button>
    );
  }

  // playing
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative mb-8 mx-auto max-w-md rounded-2xl overflow-hidden bg-black border border-white/10"
    >
      <video
        ref={vidRef}
        src={videoSrc}
        playsInline
        controlsList="nodownload noremoteplayback noplaybackrate"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onEnded={async () => {
          await consume();
          setStage("gone");
        }}
        onError={() => setStage("error")}
        className="w-full aspect-[4/5] object-cover bg-black select-none"
        data-testid="reveal-video-player"
      />
      {/* Corner label */}
      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/60 backdrop-blur px-2 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D8D] animate-pulse" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white">Live · view once</span>
      </div>
      {/* Diagonal watermark */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none flex items-center justify-center"
        style={{ transform: "rotate(-22deg)" }}
      >
        <p
          className="font-display font-bold text-white/20 text-2xl tracking-widest whitespace-nowrap"
          style={{ textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}
        >
          VibeCheck · {typerName} · R{roundNumber}
        </p>
      </div>
    </motion.div>
  );
}


function LobbyPackTile({ pack, unlocked, active, onSelect, onLocked }) {
  const [shaking, setShaking] = useState(false);
  const premium = !unlocked;
  return (
    <motion.button
      animate={shaking ? { x: [0, -8, 8, -5, 5, 0] } : { x: 0 }}
      transition={{ duration: 0.38 }}
      onClick={() => {
        if (premium) {
          setShaking(true);
          try { navigator.vibrate?.(18); } catch (_e) { /* noop */ }
          setTimeout(() => { setShaking(false); onLocked(); }, 300);
          return;
        }
        onSelect();
      }}
      data-testid={`pack-${pack.id}-button`}
      className={`relative text-left p-4 rounded-2xl border transition overflow-hidden ${
        active ? "bg-white/10 border-white/30" : premium ? "border-[#FF4D8D]/50" : "bg-white/[0.03] border-white/5 hover:bg-white/5"
      }`}
      style={
        active
          ? { boxShadow: `0 0 24px ${pack.color}44`, borderColor: `${pack.color}80` }
          : premium
          ? {
              background: "linear-gradient(135deg, rgba(255,77,141,0.16), rgba(123,97,255,0.16))",
              boxShadow: "0 0 30px rgba(255,77,141,0.30)",
            }
          : undefined
      }
    >
      {premium && (
        <>
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-2xl pointer-events-none"
            animate={{ opacity: [0.35, 0.7, 0.35] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            style={{ boxShadow: "inset 0 0 40px rgba(255,77,141,0.30)" }}
          />
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-2xl pointer-events-none"
            animate={{ backgroundPositionX: ["0%", "200%"] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            style={{
              background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.20) 50%, transparent 100%)",
              backgroundSize: "45% 100%",
              backgroundRepeat: "no-repeat",
            }}
          />
        </>
      )}
      <div className="relative">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-2xl" style={premium ? { filter: "drop-shadow(0 0 8px rgba(255,77,141,0.7))" } : undefined}>
            {pack.emoji}
          </span>
          <span className="font-bold text-white flex items-center gap-1.5 tracking-tight">
            {pack.name}
            {premium && <Lock className="w-3.5 h-3.5 text-[#FF4D8D]" />}
          </span>
          <span
            className="inline-flex items-center text-[9px] font-black tracking-[0.12em] px-1.5 py-[1px] rounded-full uppercase ml-auto"
            style={
              pack.tier === "premium"
                ? {
                    background: "linear-gradient(135deg, #FF2E9A, #FF6B47)",
                    color: "#FFFFFF",
                    border: "1px solid rgba(255, 255, 255, 0.32)",
                    boxShadow: "0 0 8px rgba(255, 46, 154, 0.6)",
                  }
                : {
                    background: "rgba(61, 214, 140, 0.16)",
                    color: "#7DE6A9",
                    border: "1px solid rgba(61, 214, 140, 0.6)",
                  }
            }
            data-testid={`lobby-pack-tier-${pack.id}`}
          >
            {pack.tier === "premium" ? "💎" : "✓ Free"}
          </span>
        </div>
        <p
          className="text-xs leading-snug"
          style={premium ? { color: "rgba(255,255,255,0.92)" } : { color: "#C9C7D9" }}
        >
          {premium ? `"${pack.description}"` : pack.description}
        </p>
        {premium && (
          <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF4D8D]">
            Tap to unlock 🔥
          </p>
        )}
      </div>
    </motion.button>
  );
}
